import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { FiCalendar, FiArrowRight, FiExternalLink, FiInbox } from 'react-icons/fi';
import { db } from '../firebase-config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import portadaImg from '../assets/images/portada.webp';
import '../styles/Noticias.css';

const NOTICIAS_POR_PAGINA = 9;

const NoticiaItem = ({ noticia }) => {
  const isExternal = noticia.externalLink && noticia.externalLink.trim() !== '';
  
  const [isClamped, setIsClamped] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const excerptRef = useRef(null);

  useEffect(() => {
    if (excerptRef.current) {
      if (excerptRef.current.scrollHeight > excerptRef.current.clientHeight) {
        setShowButton(true);
      }
    }
  }, [noticia.resumen]);

  const cardContent = (
    <>
      <div className="card-image-link-container">
        <img src={noticia.imagen} alt={noticia.titulo} className="card-image" />
      </div>
      <div className="card-content">
        <div className="card-meta">
          <span className="card-category">{noticia.categoria}</span>
          <span className="card-date"><FiCalendar /> {noticia.fechaPublicacion ? new Date(noticia.fechaPublicacion.seconds * 1000).toLocaleDateString('es-ES') : ''}</span>
        </div>
        <h3 className="card-title">
          {noticia.titulo}
        </h3>
        
        <div className="excerpt-container">
          <p
            ref={excerptRef}
            className={`card-excerpt ${isClamped ? 'clamped' : ''}`}
          >
            {noticia.resumen}
          </p>
          {showButton && (
            <button onClick={() => setIsClamped(!isClamped)} className="btn-ver-mas">
              {isClamped ? 'Ver más' : 'Ver menos'}
            </button>
          )}
        </div>
        
        <div className="card-read-more">
          {isExternal ? 'Ver Noticia Original' : 'Leer Más'}
          {isExternal ? <FiExternalLink className="arrow-icon" /> : <FiArrowRight className="arrow-icon" />}
        </div>
      </div>
    </>
  );

  return (
    <motion.div
      className="card"
      layout
      initial={{ opacity: 0, y: 30, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.98 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {isExternal ? (
        <a href={noticia.externalLink} target="_blank" rel="noopener noreferrer" className="card-link-wrapper">
          {cardContent}
        </a>
      ) : (
        <Link to={`/noticia/${noticia.id}`} className="card-link-wrapper">
          {cardContent}
        </Link>
      )}
    </motion.div>
  );
};

const SkeletonCard = () => (
    <div className="card skeleton">
      <div className="skeleton-image"></div>
      <div className="card-content">
        <div className="skeleton-line skeleton-category"></div>
        <div className="skeleton-line skeleton-title"></div>
        <div className="skeleton-line skeleton-excerpt"></div>
        <div className="skeleton-line skeleton-excerpt short"></div>
      </div>
    </div>
);

const Noticias = () => {
  const [allNoticias, setAllNoticias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('Todas');
  const [noticiasVisibles, setNoticiasVisibles] = useState(NOTICIAS_POR_PAGINA);

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchNoticias = async () => {
      try {
        setLoading(true);
        const noticiasCollectionRef = collection(db, 'noticias');
        const q = query(noticiasCollectionRef, orderBy('timestamp', 'desc'));
        const data = await getDocs(q);
        const noticiasDesdeDB = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setAllNoticias(noticiasDesdeDB);
      } catch (error) {
        console.error("Error al obtener las noticias:", error);
      } finally {
        setTimeout(() => setLoading(false), 500);
      }
    };
    fetchNoticias();
  }, []);

  const noticiasFiltradas = filtro === 'Todas'
    ? allNoticias
    : allNoticias.filter(n => n.categoria === filtro);

  const cargarMasNoticias = () => {
    setNoticiasVisibles(prev => prev + NOTICIAS_POR_PAGINA);
  };

  return (
    <div className="noticias-page" ref={containerRef}>
        <motion.div
          className="noticias-background-image"
          style={{
            backgroundImage: `url(${portadaImg})`,
            y: parallaxY
          }}
        />
        <div className="noticias-container">
            <header className="noticias-header">
            <h1>Centro de Noticias</h1>
            <p>Explore las últimas actualizaciones, comunicados y análisis del sector salud.</p>
            </header>

            <div className="filter-controls">
            <button onClick={() => setFiltro('Todas')} className={filtro === 'Todas' ? 'active' : ''}>Todas</button>
            <button onClick={() => setFiltro('Institucional')} className={filtro === 'Institucional' ? 'active' : ''}>Institucionales</button>
            <button onClick={() => setFiltro('Mix Empresarial')} className={filtro === 'Mix Empresarial' ? 'active' : ''}>Mix Empresarial</button>
            </div>

            <main>
              <AnimatePresence>
                  {loading ? (
                    <div className="skeleton-wrapper">
                      {Array.from({ length: 6 }).map((_, index) => <SkeletonCard key={index} />)}
                    </div>
                  ) : noticiasFiltradas.length > 0 ? (
                    <motion.div className="noticias-grid">
                      {noticiasFiltradas.slice(0, noticiasVisibles).map((noticia) => (
                          <NoticiaItem key={noticia.id} noticia={noticia} />
                      ))}
                    </motion.div>
                  ) : (
                    <motion.div
                      className="no-noticias-container"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <div className="no-noticias-icon"><FiInbox /></div>
                      <h3>No hay noticias todavía</h3>
                      <p>No se encontraron noticias en la categoría "{filtro}". Por favor, intente con otra categoría o vuelva más tarde.</p>
                    </motion.div>
                  )}
              </AnimatePresence>
            </main>

            {!loading && noticiasVisibles < noticiasFiltradas.length && (
            <div className="load-more-wrapper">
                <button onClick={cargarMasNoticias} className="load-more-btn">
                Cargar más noticias
                </button>
            </div>
            )}
        </div>
    </div>
  );
};

export default Noticias;